import React from 'react';
import { Box, TableRow, TableCell, Typography, IconButton, Tooltip, Button } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MapIcon from '@mui/icons-material/Map';
import MatchEventRow from './MatchEventRow';
import GroupMatchEventRow from './GroupMatchEventRow';
import NoteEventRow from './NoteEventRow';

/**
 * AssignmentEventRow - Renders an assignment event with expandable children
 *
 * Features:
 * - Shows assignment description and status (Open/Closed)
 * - Displays count of child events (matches, notes, groups)
 * - Expand/collapse to show/hide children
 * - Children render as nested MatchEventRow components
 *
 * Optimized with React.memo to prevent unnecessary re-renders.
 * Only re-renders when:
 * - Assignment event data changes (status, description)
 * - Expanded state changes
 * - Number of children changes
 */
const AssignmentEventRow = React.memo(function AssignmentEventRow({
  event,
  childEvents = [],
  expanded = false,
  onToggleExpand,
  getAddress,
  onLocationClick,
  onAssignmentClick
}) {
  const timestamp = new Date(event.timestamp || event.createdAt);
  const statusLabel = event.status === 'closed' ? 'Closed' : 'Open';
  const username = event.userHandle || event.userEmail || event.userId;

  // Calculate child statistics
  const matchCount = childEvents.filter(e => e.eventType === 'match').length;
  const groupMatchCount = childEvents.filter(e => e.eventType === 'group_match').length;
  const noteCount = childEvents.filter(e => e.eventType === 'note').length;
  const totalChildren = childEvents.length;

  // Background color for assignment rows
  const backgroundColor = '#eceff1'; // Cool grey

  return (
    <>
      {/* Assignment Header Row */}
      <TableRow
        hover
        onClick={onToggleExpand}
        sx={{
          backgroundColor,
          borderLeft: '4px solid #ff9800',
          cursor: 'pointer',
          '& .MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 0.4)' },
          '&:hover': {
            backgroundColor,
            filter: 'brightness(0.98)'
          }
        }}
      >
        {/* Event Type Icon */}
        <TableCell align="center" sx={{ width: 40 }}>
          <Tooltip title="Assignment">
            <AssignmentIcon sx={{ color: 'warning.main', fontSize: 28 }} />
          </Tooltip>
        </TableCell>

        {/* Expand/Collapse Button */}
        <TableCell align="center" sx={{ width: 40 }}>
          {totalChildren > 0 && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand();
              }}
              sx={{ p: 0.5 }}
            >
              {expanded ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
          )}
        </TableCell>

        {/* Assignment Details */}
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant="body2" fontWeight={500}>
                {event.description} ({statusLabel})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                by {username}
              </Typography>
            </Box>

            {/* Child Event Counts */}
            {totalChildren > 0 && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  {matchCount} match{matchCount !== 1 ? 'es' : ''}
                  {groupMatchCount > 0 && `, ${groupMatchCount} group${groupMatchCount !== 1 ? 's' : ''}`}
                  {noteCount > 0 && `, ${noteCount} note${noteCount !== 1 ? 's' : ''}`}
                </Typography>
              </Box>
            )}
          </Box>
        </TableCell>

        {/* Timestamp */}
        <TableCell sx={{ width: 140 }}>
          <Typography variant="caption">
            {timestamp.toLocaleString()}
          </Typography>
        </TableCell>

        {/* User Account */}
        <TableCell sx={{ width: 120 }}>
          <Typography variant="caption" color="text.secondary">
            {event.userHandle || event.userEmail || event.userId || '—'}
          </Typography>
        </TableCell>

        {/* Location - Show map icon for assignments with child events */}
        <TableCell>
          {totalChildren > 0 ? (
            <Tooltip title={`Map all Matches in assignment ${event.description}`}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onAssignmentClick && onAssignmentClick(event);
                }}
                sx={{ p: 0.5 }}
              >
                <MapIcon fontSize="small" color="primary" />
              </IconButton>
            </Tooltip>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No events
            </Typography>
          )}
        </TableCell>
      </TableRow>

      {/* Child Event Rows (only render when expanded) */}
      {expanded && childEvents.map((childEvent) => {
        if (childEvent.eventType === 'match') {
          return (
            <MatchEventRow
              key={childEvent.eventId}
              event={childEvent}
              isChild={true}
              address={getAddress(childEvent)}
              onLocationClick={onLocationClick}
            />
          );
        }

        if (childEvent.eventType === 'group_match') {
          return (
            <GroupMatchEventRow
              key={childEvent.eventId}
              event={childEvent}
              isChild={true}
              address={getAddress(childEvent)}
              onLocationClick={onLocationClick}
            />
          );
        }

        if (childEvent.eventType === 'note') {
          return (
            <NoteEventRow
              key={childEvent.eventId}
              event={childEvent}
              isChild={true}
            />
          );
        }

        // Fallback for unknown event types
        return (
          <TableRow
            key={childEvent.eventId}
            sx={{
              backgroundColor: '#f5f5f5',
              borderLeft: '4px solid #ffcc80'
            }}
          >
            <TableCell colSpan={5}>
              <Typography variant="caption" color="text.secondary">
                Unknown type: {childEvent.eventType}
              </Typography>
            </TableCell>
          </TableRow>
        );
      })}
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Return true to SKIP re-render, false to re-render
  return (
    prevProps.event.eventId === nextProps.event.eventId &&
    prevProps.event.status === nextProps.event.status &&
    prevProps.event.description === nextProps.event.description &&
    prevProps.event.updatedAt === nextProps.event.updatedAt &&
    prevProps.expanded === nextProps.expanded &&
    prevProps.childEvents?.length === nextProps.childEvents?.length
  );
});

export default AssignmentEventRow;
